import React, { type ChangeEvent, useCallback, useEffect } from 'react';
import { ChatAttachOverlays } from './ChatAttachOverlays';
import { ChatComposer } from './ChatComposer';
import { ChatInputPopup } from './ChatInputPopup';
import { EmojiPicker } from './EmojiPicker';
import { QuickReplyPicker } from './QuickReplyPicker';
import type { useChatAttachFlow } from '../../hooks/useChatAttachFlow';
import { useChatInput } from '../../hooks/useChatInput';
import type { ChatContactFormInput } from './ChatContactForm';
import type { ChatPixFormInput } from './ChatPixForm';
import type { ChatLocationFormInput } from './ChatLocationForm';

type ChatAttachFlow = ReturnType<typeof useChatAttachFlow>;

interface ChatInputProps {
    chatPlatform?: 'whatsapp' | 'instagram';
    messageText: string;
    setMessageText: (text: string) => void;
    sendValidationError?: string | null;
    handleSend: () => void;
    /** Fluxo de anexo (picker/paste/drop) — dono: ChatWindow. */
    attach: ChatAttachFlow;
    /** Texto do composer → legenda ao anexar (picker/paste). */
    absorbComposerTextAsCaption: () => void;
    onSendMedia?: (file: File, caption?: string) => void;
    onSendContact?: (input: ChatContactFormInput) => Promise<void>;
    onSendLocation?: (input: ChatLocationFormInput) => Promise<void>;
    onSendPix?: (input: ChatPixFormInput) => Promise<void>;
    isPopup?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    chatPlatform = 'whatsapp',
    messageText,
    setMessageText,
    sendValidationError,
    handleSend,
    attach,
    absorbComposerTextAsCaption,
    onSendMedia,
    onSendContact,
    onSendLocation,
    onSendPix,
    isPopup,
}) => {
    const handleMediaChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files) {
                absorbComposerTextAsCaption();
                attach.onFilesSelected(files, 'media');
                e.target.value = '';
            }
        },
        [absorbComposerTextAsCaption, attach],
    );

    const handleDocumentChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (files) {
                absorbComposerTextAsCaption();
                attach.onFilesSelected(files, 'document');
                e.target.value = '';
            }
        },
        [absorbComposerTextAsCaption, attach],
    );

    const handlePasteFiles = useCallback(
        (files: File[]) => {
            absorbComposerTextAsCaption();
            attach.stageIncomingFiles(files);
        },
        [absorbComposerTextAsCaption, attach],
    );

    const guardedSend = useCallback(() => {
        if (attach.isPreviewOpen) return;
        handleSend();
    }, [attach.isPreviewOpen, handleSend]);

    const input = useChatInput({
        messageText,
        setMessageText,
        onSend: guardedSend,
        onSendMedia,
        onPasteFiles: handlePasteFiles,
    });

    if (isPopup) {
        return (
            <ChatInputPopup
                messageText={messageText}
                setMessageText={setMessageText}
                sendValidationError={sendValidationError}
                handleSend={handleSend}
            />
        );
    }

    const previewOpen = attach.isPreviewOpen;

    // Preview esconde o composer: se o foco ficou no trigger Radix (ex. Anexar),
    // aria-hidden no ancestral dispara "Blocked aria-hidden… descendant retained focus".
    useEffect(() => {
        if (!previewOpen) return;
        const active = document.activeElement;
        if (active instanceof HTMLElement) active.blur();
    }, [previewOpen]);

    return (
        <div
            className={
                previewOpen
                    ? 'relative shrink-0'
                    : 'relative shrink-0 bg-chat-window-bg px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:pb-2'
            }
        >
            <div className="w-full">
                {!previewOpen && input.showQuickReplies && input.quickReplySearchTerm !== null && (
                    <QuickReplyPicker
                        searchTerm={input.quickReplySearchTerm}
                        onSelect={input.handleQuickReplySelect}
                        onClose={input.handleQuickReplyClose}
                    />
                )}

                {/* Esconde o composer no preview (inputs file ficam no DOM p/ “adicionar mais”).
                    Só `hidden` — aria-hidden no ancestral com foco no Anexar (Radix) gera o warning. */}
                <div className={previewOpen ? 'hidden' : undefined}>
                    <ChatComposer
                        chatPlatform={chatPlatform}
                        messageText={messageText}
                        setMessageText={setMessageText}
                        sendValidationError={sendValidationError}
                        isRecording={input.isRecording}
                        isPaused={input.isPaused}
                        isPreviewPlaying={input.isPreviewPlaying}
                        canResumePreview={input.canResumePreview}
                        previewProgress={input.previewProgress}
                        recordingTime={input.recordingTime}
                        formatTime={input.formatTime}
                        cancelRecording={input.cancelRecording}
                        pauseRecording={input.pauseRecording}
                        resumeRecording={input.resumeRecording}
                        togglePreviewPlayback={input.togglePreviewPlayback}
                        seekPreview={input.seekPreview}
                        showSendButton={input.showSendButton}
                        startRecording={input.startRecording}
                        stopRecording={input.stopRecording}
                        handleSend={guardedSend}
                        toggleEmojiPicker={input.toggleEmojiPicker}
                        showEmojiPicker={input.showEmojiPicker}
                        emojiButtonRef={input.emojiButtonRef}
                        mediaInputRef={attach.mediaInputRef}
                        documentInputRef={attach.documentInputRef}
                        onMediaChange={handleMediaChange}
                        onDocumentChange={handleDocumentChange}
                        onOpenContact={() => attach.openSpecial('contact')}
                        onOpenPix={() => attach.openSpecial('pix')}
                        onOpenLocation={() => attach.openSpecial('location')}
                        handleKeyDown={input.handleKeyDown}
                        handlePaste={input.handlePaste}
                    />
                </div>

                {!previewOpen && input.showEmojiPicker && (
                    <EmojiPicker
                        onEmojiSelect={input.handleEmojiSelect}
                        onClose={() => input.setShowEmojiPicker(false)}
                        triggerRef={input.emojiButtonRef}
                    />
                )}

                <ChatAttachOverlays
                    attach={attach}
                    onSendContact={onSendContact}
                    onSendLocation={onSendLocation}
                    onSendPix={onSendPix}
                />
            </div>
        </div>
    );
};
